import { loginRequest } from "../settings";

/**
 * fetch() wrapper for calls to this app's own /api/* backend.
 *
 * Attaches the signed-in user's Azure AD ID token as a Bearer token so the
 * server can verify the caller's identity (see auth.js on the backend) instead
 * of trusting a client-supplied email/role. Must be called with the `instance`
 * and `account` from useMsal() — those come from the same PublicClientApplication
 * created in index.js, so the token cache is the real one for the signed-in session.
 *
 * Requests the exact same scopes used for the interactive sign-in
 * (loginRequest — openid/profile/email, see App.js's MsalAuthenticationTemplate).
 * Those are guaranteed to already be granted for this session, so
 * acquireTokenSilent can satisfy the request from cache or via a normal
 * refresh without hitting Azure AD with a scope it doesn't recognize.
 * (This app registration has no "Expose an API" scope configured, so
 * requesting a custom/self scope — e.g. the client ID — is rejected by
 * Azure AD with a 400 invalid_scope error; don't reintroduce that.)
 * The ID token's audience is always the client ID regardless of which
 * scope was used to request it, so the backend's audience check is unaffected.
 *
 * acquireTokenSilent's cache-hit path only checks the cached ACCESS token's
 * expiry (see @azure/msal-common's SilentFlowClient.acquireCachedToken) — it
 * never checks the cached ID token's own exp claim. If this tenant's access
 * token lifetime outlives its ID token lifetime, a "valid" cache hit can still
 * hand back a stale, already-expired ID token, which the backend then rejects
 * with "jwt expired". So check the cached ID token's own expiry ourselves and
 * force a real network refresh when it's stale or about to go stale.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {import("@azure/msal-browser").AccountInfo} [account]
 */
const MIN_VALID_MS = 2 * 60 * 1000;

export async function apiFetch(url, options, instance, account) {
  const activeAccount = account || instance.getActiveAccount();
  if (!activeAccount) {
    throw new Error("No signed-in account available for API call.");
  }

  const cachedExpSeconds = activeAccount.idTokenClaims?.exp;
  const forceRefresh = !cachedExpSeconds || cachedExpSeconds * 1000 - Date.now() < MIN_VALID_MS;

  const { idToken } = await instance.acquireTokenSilent({
    account: activeAccount,
    scopes: loginRequest.scopes,
    forceRefresh,
  });

  return fetch(url, {
    ...options,
    headers: {
      ...(options?.headers || {}),
      Authorization: `Bearer ${idToken}`,
    },
  });
}
